import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Timestamp } from 'firebase/firestore';
import { ChevronLeft, ChevronRight, ArrowLeft, CalendarDays } from 'lucide-react';
import { jobsCol } from './database.js';
import { updateOne } from './lib/db-crud.js';
import { useUserData } from './lib/use-user-data.js';
import { useBusinessProfile } from './lib/use-business-profile.js';
import { STATUS_TEXT, flatMapVisits, jobsForActiveCustomers } from './lib/job-helpers.js';

import JobLeaveModal from './JobLeaveModal.jsx';
import ConfirmDialog from './ConfirmDialog.jsx';

import { DEFAULT_DURATION, startOfDay, startOfMonth, addDays, addMonths, formatDayLabel, formatMonthLabel, toDate, dateFromInputValue } from './lib/calendar-view.js';
import { DayView, MonthView } from './CalendarViews.jsx';
import { JobSidebar, ReturnVisitSidebar } from './CalendarSidebars.jsx';
// ─── Top-level Calendar screen ───────────────────────────────────────────
export default function Calendar() {
  const { jobs: allJobs, customers, allCustomers, vehicles, ready } = useUserData();

  // An archived customer's appointments come off the calendar with
  // them. Restoring the customer puts them back — nothing is deleted.
  const jobs = useMemo(() => jobsForActiveCustomers(allJobs, allCustomers), [allJobs, allCustomers]);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Default view is Month — Day is a drill-down you reach by tapping a cell.
  // Optional deep-link from Home: /calendar?date=YYYY-MM-DD&view=day
  const initialView = searchParams.get('view') === 'day' ? 'day' : 'month';
  const initialDate = (() => {
    const d = searchParams.get('date');
    if (d && /^\d{4}-\d{2}-\d{2}$/.test(d)) {
      const parsed = dateFromInputValue(d);
      if (!isNaN(parsed.getTime())) return startOfDay(parsed);
    }
    return startOfDay(new Date());
  })();

  const [view, setView] = useState(initialView); // 'day' | 'month'
  const [anchorDate, setAnchorDate] = useState(initialDate);
  const [sidebar, setSidebar] = useState(null); // null | { mode, initial }
  const [leaveJob, setLeaveJob]         = useState(null); // job currently being Left from the calendar
  const [pendingStart, setPendingStart] = useState(null); // job awaiting "not approved" confirmation
  const { profile: businessProfile }    = useBusinessProfile();

  // Strip the deep-link params after first read so refresh / nav stays
  // on whatever the user has navigated to since.
  useEffect(() => {
    if (searchParams.get('date') || searchParams.get('view') || searchParams.get('new')) {
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ?new=1 — Home's "+ New Appointment" button lands here. Open the
  // create sidebar at the next available 9 AM slot. Only fires once,
  // on first mount.
  useEffect(() => {
    if (searchParams.get('new') !== '1') return;
    const start = new Date(initialDate);
    start.setHours(9, 0, 0, 0);
    setSidebar({
      mode: 'create',
      initial: {
        scheduledAt: start,
        durationMinutes: DEFAULT_DURATION,
        customerId: '',
        vehicleId: '',
        description: '',
        totalPrice: '',
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Calendar blocks — flatMapVisits expands each job into one block per
  // initial visit + return visit. Returns blocks carry _visitKind so
  // the renderer can flag returns with ↩ and the tap handler can pick
  // the right editor (full sidebar vs. ReturnVisitSidebar).
  const scheduledJobs = useMemo(() => flatMapVisits(jobs, DEFAULT_DURATION), [jobs]);

  function navPrev() {
    if (view === 'day') setAnchorDate((d) => addDays(d, -1));
    else setAnchorDate((d) => addMonths(d, -1));
  }
  function navNext() {
    if (view === 'day') setAnchorDate((d) => addDays(d, 1));
    else setAnchorDate((d) => addMonths(d, 1));
  }
  function navToday() { setAnchorDate(startOfDay(new Date())); }

  function drillToDay(date) {
    setAnchorDate(startOfDay(date));
    setView('day');
  }

  function openCreate(start, durationMinutes = DEFAULT_DURATION) {
    // Nothing to open if they can't create the job at the end of it —
    // better a slot that doesn't respond than a form that fails on
    // save. The banner at the top already says why.
    setSidebar({
      mode: 'create',
      initial: {
        scheduledAt: start,
        durationMinutes,
        customerId: customers[0]?.id || '',
        vehicleId: '',
        description: '',
        totalPrice: '',
      },
    });
  }
  function openEdit(block) {
    // Return-visit blocks open the smaller ReturnVisitSidebar — they live
    // on the parent job's returnVisits[] array, not as their own job doc.
    if (block._visitKind && block._visitKind !== 'initial') {
      setSidebar({
        mode: 'edit-return',
        initial: {
          jobId: block.id,
          visitId: block._visitId,
          customerName: customers.find((c) => c.id === block.customerId)?.name || '',
          description: block.description,
          scheduledAt: block._start,
          durationMinutes: block._durationMinutes,
          kind: block._visitKind,
          notes: block._visitNotes || '',
        },
      });
      return;
    }
    setSidebar({
      mode: 'edit',
      initial: {
        id: block.id,
        scheduledAt: toDate(block.scheduledAt),
        durationMinutes: block.durationMinutes || DEFAULT_DURATION,
        customerId: block.customerId || '',
        vehicleId: block.vehicleId || '',
        description: block.description || '',
        // The priced contents of the job. These MUST be here.
        //
        // They were missing, and the omission silently destroyed data:
        // the sidebar seeds its editor from `initial`, so both came up
        // empty, and Save wrote the empty version back over a real
        // breakdown. Open a priced job from the calendar, change
        // nothing but the time, hit Save, and its parts, labor and
        // price were gone — with the job still sitting there looking
        // fine at $0.00.
        //
        // The single `totalPrice` string below is the fossil that
        // explains it: it dates from before the parts/labor split,
        // when a job's price WAS one number and passing it was enough.
        // The split replaced that field with the line-items editor and
        // nobody came back here. It stays only because it costs
        // nothing; the sidebar computes the total from the line items.
        lineItems: Array.isArray(block.lineItems) ? block.lineItems : [],
        shopSupplies: Number(block.shopSupplies) || 0,
        tax: block.tax || null,
        totalPrice: String(block.totalPrice ?? ''),
      },
      // We DON'T snapshot the job here — the JobSidebar looks it up live
      // from `jobs` on every render so when handleStart updates Firestore,
      // the Start/Leave button rerenders correctly. (Snapshotting was
      // causing the button to stay "Start" after Brad tapped it.)
    });
  }

  function navLabel() {
    if (view === 'day') return formatDayLabel(anchorDate);
    return formatMonthLabel(startOfMonth(anchorDate));
  }

  // Start the work timer on a job from the Calendar sidebar. The
  // "not approved" warning uses ConfirmDialog (themed), not native
  // confirm(). Mirrors the same pattern in Home.jsx and Jobs.jsx.
  function handleStart(j) {
    if (!j) return;
    if (j.status !== 'approved' && j.status !== 'invoiced') {
      setPendingStart(j);
      return;
    }
    actuallyStart(j);
  }

  async function actuallyStart(j) {
    if (!j) return;
    try {
      await updateOne(jobsCol(), j.id, {
        startedAt:      Timestamp.now(),
        closedAt:       null,
        awaitingReturn: false,
        awaitingPart:   false,
      });
      // Close the sidebar and auto-navigate into the active workspace
      // (notes / timer / Leave). Same auto-navigate fires from Home
      // and the per-customer JobCard — single landing destination.
      setSidebar(null);
      navigate(`/active/${j.id}`);
    } catch (e) {
      alert(`Could not start: ${e.message}`);
    }
  }

  // Undo Start — clears startedAt for the "didn't mean to tap that" case.
  async function handleUndoStart(j) {
    if (!j) return;
    try {
      await updateOne(jobsCol(), j.id, { startedAt: null });
    } catch (e) {
      alert(`Could not undo: ${e.message}`);
    }
  }

  function handleLeave(j) {
    setSidebar(null);  // close the sidebar so the modal doesn't stack on top of it
    setLeaveJob(j);
  }

  return (
    <div>
      <div className="screen-header">
        <div>
          <h1>Calendar</h1>
          <p className="subtitle">
            {scheduledJobs.length === 0
              ? 'No appointments scheduled yet.'
              : `${scheduledJobs.length} scheduled appointment${scheduledJobs.length === 1 ? '' : 's'}`}
          </p>
        </div>
      </div>

      {/* Date navigation. Day view gets a "Back to month" button up front. */}
      <div className="cal-nav">
        {view === 'day' && (
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setView('month')}
            aria-label="Back to month view"
          >
            <ArrowLeft size={14} /> Month
          </button>
        )}
        <button className="btn-secondary" onClick={navPrev} aria-label="Previous">
          <ChevronLeft size={16} />
        </button>
        <div className="cal-nav-label">{navLabel()}</div>
        <button className="btn-secondary" onClick={navNext} aria-label="Next">
          <ChevronRight size={16} />
        </button>
        <button className="btn-link" onClick={navToday}>
          <CalendarDays size={14} /> Today
        </button>
      </div>

      {!ready && <p className="muted small">Loading…</p>}

      {ready && customers.length === 0 && (
        <p className="muted small empty-inline">
          <CalendarDays size={20} color="var(--text-3)" strokeWidth={1.5}
            style={{ display: 'block', margin: '0 auto 6px' }} />
          Add a customer first, then come back to schedule appointments.
        </p>
      )}

      {ready && customers.length > 0 && view === 'day' && (
        <DayView
          date={anchorDate}
          jobs={scheduledJobs}
          customers={customers}
          vehicles={vehicles}
          onSlotTap={openCreate}
          onBlockTap={openEdit}
        />
      )}

      {ready && customers.length > 0 && view === 'month' && (
        <MonthView
          monthStart={startOfMonth(anchorDate)}
          jobs={scheduledJobs}
          customers={customers}
          onDayTap={drillToDay}
        />
      )}

      {sidebar && sidebar.mode !== 'edit-return' && (
        <JobSidebar
          mode={sidebar.mode}
          initial={sidebar.initial}
          // Look the job up live from `jobs` on every render so the
          // Start/Leave button state mirrors the latest Firestore data
          // the moment handleStart updates. Was previously inside an
          // IIFE; turns out that pattern was masking the bug because
          // the lookup wasn't always consistent between the prop and
          // the onStart/onLeave closures.
          liveJobId={sidebar.initial.id || null}
          allLiveJobs={jobs}
          customers={customers}
          vehicles={vehicles}
          allJobs={scheduledJobs}
          laborPresets={businessProfile.laborPresets || []}
          taxPresets={businessProfile.taxPresets || []}
          partSearchLinks={businessProfile.partSearchLinks}
          onClose={() => setSidebar(null)}
          onStart={(j) => handleStart(j)}
          onLeave={(j) => handleLeave(j)}
          onUndoStart={(j) => handleUndoStart(j)}
        />
      )}

      {sidebar && sidebar.mode === 'edit-return' && (
        <ReturnVisitSidebar
          initial={sidebar.initial}
          allBlocks={scheduledJobs}
          jobs={jobs}
          onClose={() => setSidebar(null)}
        />
      )}

      {pendingStart && (
        <ConfirmDialog
          title="Job not approved yet"
          body={`This job is still in "${STATUS_TEXT[pendingStart.status] || 'Draft'}" status — the customer hasn't approved the estimate. You can still start working and send the estimate after.`}
          confirmLabel="Start anyway"
          cancelLabel="Cancel"
          onConfirm={() => {
            const j = pendingStart;
            setPendingStart(null);
            actuallyStart(j);
          }}
          onCancel={() => setPendingStart(null)}
        />
      )}

      {leaveJob && (
        <JobLeaveModal
          job={leaveJob}
          customer={customers.find((c) => c.id === leaveJob.customerId) || null}
          businessProfile={businessProfile}
          onClose={() => setLeaveJob(null)}
        />
      )}
    </div>
  );
}

// ─── Day view ─────────────────────────────────────────────────────────────
